/**
 * authGateEvents — helpers específicos del funnel auth_gate (card N52QMDjD).
 *
 * Sigue el mismo patrón que ``wizardEvents.ts`` (card hermana KEQz7YBQ) y
 * consume el helper compartido ``trackEvent``. Este modulo agrupa los 5
 * eventos GA4 del funnel de conversion post-wizard:
 *
 *   - ``auth_gate_shown``       — el ``AuthGateBlock`` monta en UI. Idempotente
 *                                 por session_id (flag ``sessionStorage``).
 *   - ``signup_started``        — click "Crear cuenta gratis". Se dispara ANTES
 *                                 del ``loginWithRedirect`` para no perder el
 *                                 evento si el redirect tarda.
 *   - ``signup_completed``      — Auth0 callback exitoso. El caller lo dispara
 *                                 en el ``useEffect`` que detecta el flip de
 *                                 ``isAuthenticated``. Trae ``user_id`` (Auth0
 *                                 sub, NO email) + ``email_verified``.
 *   - ``auth_gate_skipped``     — click "Continuar sin guardar" (secondary CTA).
 *                                 Idempotente por session_id.
 *   - ``prospect_claimed``      — ``POST /api/v1/prospects/claim`` devolvio 200.
 *                                 Cierra el loop wizard → signup → claim.
 *                                 Idempotente por session_id.
 *
 * ``wizard_completed`` NO vive aca — ya lo emite ``wizardEvents.ts`` cuando
 * aparece el bloque de cierre (``auth_gate`` / ``lead_capture`` /
 * ``vis_advisory_summary``). Reutilizamos ese wire, no lo duplicamos.
 *
 * Reglas criticas (spec padre apI97mhv — analitica en 2 niveles):
 *
 *   - **Sin PII**: los payloads nunca incluyen email, nombre, telefono ni
 *     texto libre. ``user_id`` es el ``sub`` de Auth0 (identificador opaco
 *     tipo ``auth0|abc123`` / ``google-oauth2|xyz``) — NO el email.
 *   - ``session_id`` es el ``conversation_id`` opaco del chat (mismo valor que
 *     usa ``wizardEvents.ts``). Nunca email/nombre.
 *   - **Idempotencia**: cada evento singleton (shown / started / skipped /
 *     completed / claimed) usa un flag en ``sessionStorage`` con clave
 *     ``auth_gate:<event>:<session_id>``. Un session_id que ya emitio el
 *     evento no lo re-emite en re-mounts, dev hot-reload, o re-render de
 *     StrictMode.
 *   - **Silent no-op**: sin ``session_id`` el helper no emite. Sin
 *     ``window.gtag`` el helper ``trackEvent`` es no-op — nunca rompe la UI.
 *
 * Validacion en produccion: los eventos aparecen en GA4 → Realtime bajo el
 * nombre exacto. Filtrar por ``session_id`` para inspeccionar el funnel de
 * un usuario especifico (ver change doc para la query GA4 esperada).
 */

import { trackEvent } from './trackEvent';

const SHOWN_KEY = (sessionId: string): string =>
  `auth_gate:shown:${sessionId}`;
const STARTED_KEY = (sessionId: string): string =>
  `auth_gate:signup_started:${sessionId}`;
const COMPLETED_KEY = (sessionId: string): string =>
  `auth_gate:signup_completed:${sessionId}`;
const SKIPPED_KEY = (sessionId: string): string =>
  `auth_gate:skipped:${sessionId}`;
const CLAIMED_KEY = (sessionId: string): string =>
  `auth_gate:prospect_claimed:${sessionId}`;

/**
 * Lee un flag de ``sessionStorage`` devolviendo ``false`` si el storage no
 * esta disponible (SSR, jsdom sin stub, incognito bloqueado). Nunca throwa.
 */
function readFlag(key: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * Setea un flag en ``sessionStorage``. Tolerante a quota / incognito lockout
 * — analytics nunca debe romper la UI del user.
 */
function writeFlag(key: string): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Quota exceeded / incognito — swallow.
  }
}

/**
 * ``auth_gate_shown`` — el ``AuthGateBlock`` renderiza en UI (mount).
 *
 * Idempotente por session_id: si el usuario navega dentro del chat y el
 * bloque re-monta, no re-emitimos. El flag persiste dentro del tab.
 *
 * Payload:
 *   - ``session_id``: opaco (mismo que usa el wizard)
 *   - ``conversation_id``: id del hilo chat (en este codebase == session_id,
 *     lo mandamos redundante porque la spec padre apI97mhv distingue ambos
 *     conceptualmente y GA4 debe poder filtrar por conversation_id aunque
 *     internamente sean el mismo string).
 */
export function emitAuthGateShown(
  sessionId: string,
  conversationId: string,
): void {
  if (!sessionId || !conversationId) return;
  const key = SHOWN_KEY(sessionId);
  if (readFlag(key)) return;
  writeFlag(key);
  trackEvent('auth_gate_shown', {
    session_id: sessionId,
    conversation_id: conversationId,
  });
}

/**
 * ``signup_started`` — click en "Crear cuenta gratis" (primary CTA).
 *
 * Dispara ANTES de ``loginWithRedirect`` para no perder el hit si el
 * redirect tarda o falla. Idempotente por session_id — si el user vuelve
 * al mismo tab despues del redirect y clickea de nuevo, no re-emitimos.
 */
export function emitSignupStarted(sessionId: string): void {
  if (!sessionId) return;
  const key = STARTED_KEY(sessionId);
  if (readFlag(key)) return;
  writeFlag(key);
  trackEvent('signup_started', { session_id: sessionId });
}

/**
 * ``signup_completed`` — Auth0 callback exitoso post-signup.
 *
 * Se dispara desde el ``useEffect`` que detecta el flip de
 * ``isAuthenticated`` a ``true``. El caller resuelve ``userId`` desde
 * ``useAuth0().user?.sub`` — nunca del email.
 *
 * Idempotente por session_id: en StrictMode / hot-reload el efecto corre
 * dos veces; el flag garantiza un solo hit por conversation.
 *
 * Payload:
 *   - ``session_id``: opaco
 *   - ``user_id``: Auth0 sub (``auth0|...`` / ``google-oauth2|...``), NO email
 *   - ``email_verified``: bool (util para segmentar signup con verificacion
 *     pendiente vs verificado — ambos completan signup)
 */
export function emitSignupCompleted(
  sessionId: string,
  userId: string,
  emailVerified: boolean,
): void {
  if (!sessionId || !userId) return;
  const key = COMPLETED_KEY(sessionId);
  if (readFlag(key)) return;
  writeFlag(key);
  trackEvent('signup_completed', {
    session_id: sessionId,
    user_id: userId,
    email_verified: emailVerified,
  });
}

/**
 * ``auth_gate_skipped`` — click en "Continuar sin guardar" (secondary CTA).
 *
 * Idempotente por session_id — el bloque se dismisses localmente asi que
 * un segundo click no deberia pasar, pero cubrimos StrictMode / re-mount.
 */
export function emitAuthGateSkipped(sessionId: string): void {
  if (!sessionId) return;
  const key = SKIPPED_KEY(sessionId);
  if (readFlag(key)) return;
  writeFlag(key);
  trackEvent('auth_gate_skipped', { session_id: sessionId });
}

/**
 * ``prospect_claimed`` — ``POST /api/v1/prospects/claim`` devolvio 200.
 *
 * Cierra el loop del funnel: wizard anonimo → signup → claim linkeado. El
 * caller (hook ``useProspectClaim``) lo dispara despues de recibir el 200.
 *
 * Idempotente por session_id — el hook ya tiene un flag para no reclamar
 * dos veces, pero cubrimos por si el flag falla (private mode).
 *
 * Payload:
 *   - ``session_id``: opaco
 *   - ``user_id``: Auth0 sub, NO email
 *   - ``materialized``: bool — true cuando el backend creo un ``prospect``
 *     nuevo a partir del ``wizard_responses`` huerfano (outcome=``claimed``);
 *     false cuando el prospect ya existia (outcome=``already_owned``). Util
 *     para medir "cold conversions" del funnel.
 */
export function emitProspectClaimed(
  sessionId: string,
  userId: string,
  materialized: boolean,
): void {
  if (!sessionId || !userId) return;
  const key = CLAIMED_KEY(sessionId);
  if (readFlag(key)) return;
  writeFlag(key);
  trackEvent('prospect_claimed', {
    session_id: sessionId,
    user_id: userId,
    materialized,
  });
}

/**
 * Utilitario para tests — resetea todos los flags del session_id dado.
 */
export function _resetAuthGateTrackingForTests(sessionId: string): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.removeItem(SHOWN_KEY(sessionId));
    window.sessionStorage.removeItem(STARTED_KEY(sessionId));
    window.sessionStorage.removeItem(COMPLETED_KEY(sessionId));
    window.sessionStorage.removeItem(SKIPPED_KEY(sessionId));
    window.sessionStorage.removeItem(CLAIMED_KEY(sessionId));
  } catch {
    // no-op
  }
}
